import { useChartTimeNavigation } from './ChartTimeNavigation'
import { memo, useId, useMemo, useRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import type { ECharts, EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerExchangeData, ScreenerOIDataPoint } from '@/types'
import { ChartRuler, type OIRulerSeries } from './ChartRuler'
import { ChartLevelAlerts, chartAlertMarkLine, type ChartAlertControls } from './ChartLevelAlerts'
import { useChartAlertDrag } from './useChartAlertDrag'
import { useVisibleChartScale } from './useVisibleChartScale'
import { useChartResize } from './useChartResize'
import { chartTooltipTime, useChartCrosshair } from './useChartCrosshair'
import { buildOIPlot, filledOIDescription, isDisplayableOI } from './oiDisplay'
import { oiValueAxisPointer } from './oiCrosshair'
import { FundingRateBadge } from './FundingRateBadge'
import { ChartCollapseButton } from './ChartCollapseButton'
import { buildVolumeLookup, volumeAtTime, volumeTooltipRow } from './volumeDisplay'
import {
  exchangeColor,
  findLastKnownIndex,
  formatAge,
  formatCompact,
  formatUtc,
  formatUtcAxis,
  isFreshOI,
  isPlottableOI,
  oiQualityDescription,
} from './chartUtils'

interface MergedOIChartProps {
  symbol: string
  seriesByExchange: Record<string, ScreenerOIDataPoint[]>
  exchangeData: Record<string, ScreenerExchangeData>
  candleInterval?: string
  interval: string
  /** Accepted for older callers; OI charts always render as lines. */
  lineMode?: boolean
  chartGroup: string
  onCrosshairMove: (time: Time | null) => void
  syncedTime: Time | null
  height?: number
  fillThrough?: number | null
  alertControls?: ChartAlertControls
  collapsed?: boolean
  onToggleCollapse?: () => void
}

function MergedOIChartComponent({
  symbol,
  seriesByExchange,
  exchangeData,
  candleInterval,
  interval,
  chartGroup,
  onCrosshairMove,
  syncedTime,
  height = 440,
  fillThrough,
  alertControls: savedAlertControls,
  collapsed = false,
  onToggleCollapse,
}: MergedOIChartProps) {
  const chartBodyId = useId()
  const chartRef = useRef<ReactECharts>(null)
  const [rulerChart, setRulerChart] = useState<ECharts | null>(null)
  useChartResize(rulerChart)
  const timeNavigation = useChartTimeNavigation(rulerChart)
  const derived = useMemo(() => Object.entries(seriesByExchange)
    .filter(([, points]) => points.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([exchange, rawPoints], index) => {
      const raw = [...rawPoints].sort((a, b) => a.timestamp - b.timestamp)
      const plot = buildOIPlot(raw, interval, fillThrough)
      return {
        exchange,
        color: exchangeColor(index),
        rawPoints: raw,
        ...plot,
        timestamps: plot.points.map((point) => point.timestamp * 1000),
      }
    }), [seriesByExchange, interval, fillThrough])

  const scaleSeries = useMemo(() => derived.map((entry) => entry.series), [derived])
  const alertSeries = useMemo(() => derived.map((entry) => ({
    exchange: entry.exchange, points: entry.series,
  })), [derived])
  const alertControls = useChartAlertDrag(rulerChart, savedAlertControls, {
    symbol, metric: 'oi', exchanges: alertSeries.map((entry) => entry.exchange),
  })
  const { fitVisibleScale, onChartReady } = useVisibleChartScale(scaleSeries, `${symbol}:${interval}`, true)

  const rulerSeries = useMemo<OIRulerSeries[]>(() => derived.map((entry) => ({
    id: entry.exchange, label: entry.exchange, color: entry.color,
    points: entry.rawPoints.filter(isPlottableOI),
    oiData: entry.rawPoints,
    candles: exchangeData[entry.exchange]?.candles || [],
    currentPrice: exchangeData[entry.exchange]?.last_price,
  })), [derived, exchangeData])

  const hoveredValues = useMemo(() => derived.map((entry) => {
    const pointIndex = syncedTime == null
      ? entry.points.length - 1
      : findLastKnownIndex(entry.timestamps, Number(syncedTime) * 1000)
    const point = pointIndex >= 0 ? entry.points[pointIndex] : null
    return {
      ...entry,
      hovered: {
        point,
        ageSeconds: point && syncedTime != null
          ? Math.max(0, Math.floor(Number(syncedTime) - point.timestamp))
          : null,
        filledDescription: point ? filledOIDescription(point) : null,
      },
    }
  }), [derived, syncedTime])
  const latestFilled = useMemo(() => derived.filter((entry) => entry.latestFilled), [derived])
  const volumeLookups = useMemo(() => Object.fromEntries(derived.map(({ exchange }) => [exchange,
    buildVolumeLookup(exchangeData[exchange]?.volume_history, exchangeData[exchange]?.volume_interval, interval),
  ])), [derived, exchangeData, interval])

  const option = useMemo<EChartsOption>(() => ({
    animation: false,
    useUTC: true,
    backgroundColor: 'transparent',
    grid: { left: 12, right: 82, top: 10, bottom: 36, containLabel: true },
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', lineStyle: { color: '#64748b', type: 'dashed' } },
      backgroundColor: 'rgba(5, 10, 18, 0.97)',
      borderColor: '#334155',
      textStyle: { color: '#e2e8f0', fontSize: 11 },
      formatter: (raw: any) => {
        const entries = Array.isArray(raw) ? raw : [raw]
        const axisMs = chartTooltipTime(chartRef.current?.getEchartsInstance(), Number(entries[0]?.axisValue ?? 0))
        const rows = derived.map((entry) => {
          const pointIndex = findLastKnownIndex(entry.timestamps, axisMs)
          const point = pointIndex >= 0 ? entry.points[pointIndex] : null
          const volumeRow = volumeTooltipRow(volumeAtTime(volumeLookups[entry.exchange], axisMs, interval), interval)
          if (!point || !isDisplayableOI(point)) return `<div><span style="color:${entry.color}">●</span> ${entry.exchange.toUpperCase()}: N/A</div>${volumeRow}`
          const age = Math.max(0, Math.floor((axisMs - point.timestamp * 1000) / 1000))
          const stale = !isFreshOI(point)
          const qualityDescription = filledOIDescription(point) || oiQualityDescription(point)
          return `<div><span style="color:${entry.color}">●</span> ${entry.exchange.toUpperCase()}: <b>${formatCompact(point.value)} coins</b>${qualityDescription ? ` <span style="color:${stale ? '#f59e0b' : '#94a3b8'};font-size:9px">(${qualityDescription})</span>` : ''}${age > 0 ? ` <span style="color:#f59e0b;font-size:9px">(${formatAge(age)})</span>` : ''}</div>${volumeRow}`
        })
        return `<div style="color:#94a3b8;margin-bottom:6px">${formatUtc(axisMs, interval === '5s')}</div>${rows.join('')}`
      },
    },
    xAxis: {
      type: 'time',
      axisPointer: { snap: false },
      ...timeNavigation.axis,
      axisLine: { lineStyle: { color: '#253044' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        hideOverlap: true,
        formatter: (value: number) => formatUtcAxis(value, interval),
      },
      splitLine: { show: true, lineStyle: { color: '#172033', type: 'dashed' } },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      triggerEvent: true,
      scale: true,
      axisPointer: oiValueAxisPointer(),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#64748b', fontSize: 10, formatter: (value: number) => formatCompact(value) },
      splitLine: { lineStyle: { color: '#172033', type: 'dashed' } },
    },
    dataZoom: [{
      id: 'screener-time',
        ...timeNavigation.zoom,
      type: 'inside',
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseWheel: false,
      moveOnMouseMove: true,
      filterMode: 'none',
    }],
    series: derived.map((entry) => ({
      id: entry.exchange,
      name: entry.exchange,
      type: 'line',
      showSymbol: entry.points.length === 1,
      smooth: false,
      connectNulls: false,
      lineStyle: { width: 1.7, color: entry.color },
      itemStyle: { color: entry.color },
      data: entry.series,
      markLine: chartAlertMarkLine(alertControls, symbol, entry.exchange, 'oi'),
    })),
  }), [derived, interval, volumeLookups, alertControls, symbol, timeNavigation])

  useChartCrosshair(rulerChart, chartGroup, onCrosshairMove, interval)

  return (
    <div data-chart-collapsed={collapsed} className="relative overflow-hidden rounded-xl border border-slate-800/90 bg-[#080d16] shadow-[0_18px_50px_rgba(0,0,0,0.2)]">
      <div className="relative z-10 flex min-h-[58px] flex-wrap items-center gap-x-4 gap-y-1 py-3 pl-4 pr-24">
        <div className="flex items-center gap-2">
          <ChartCollapseButton collapsed={collapsed} onToggle={onToggleCollapse} label="merged OI chart" controlsId={chartBodyId} />
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Open Interest · Coins</span>
        </div>
        {hoveredValues.map((entry) => (
          <div key={entry.exchange} className="flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px]">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="uppercase text-slate-400">{entry.exchange}</span>
            <span className="font-mono font-semibold" style={{ color: entry.color }}>
              {entry.hovered.point && isDisplayableOI(entry.hovered.point) ? formatCompact(entry.hovered.point.value) : 'N/A'}
            </span>
            {entry.hovered.ageSeconds != null && entry.hovered.ageSeconds > 0 && (
              <span className="text-[9px] text-amber-400">{formatAge(entry.hovered.ageSeconds)}</span>
            )}
            {entry.hovered.filledDescription ? (
              <span title={entry.hovered.filledDescription} className="text-[9px] uppercase text-amber-400">filled</span>
            ) : entry.hovered.point && !isFreshOI(entry.hovered.point) && (
              <span className="text-[9px] uppercase text-amber-400">stale</span>
            )}
            <FundingRateBadge data={exchangeData[entry.exchange]} />
          </div>
        ))}
        {!collapsed && latestFilled.length > 0 && (
          <div className="basis-full text-[10px] leading-4 text-amber-400"
            title={latestFilled.map((entry) => `${entry.exchange.toUpperCase()}: ${filledOIDescription(entry.latestFilled!)}`).join('\n')}>
            Latest point filled with last data: {latestFilled.map((entry) => entry.exchange.toUpperCase()).join(', ')}
          </div>
        )}
      </div>
      <button type="button" onClick={fitVisibleScale}
        title="Click to fit OI scale to the visible time range"
        aria-label="Fit merged OI scale to visible time range"
        className="absolute right-3 top-3 z-10 rounded-md border border-slate-700/70 bg-slate-950/75 px-2 py-1 text-[10px] text-slate-300 hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300">
        Fit scale
      </button>
      <div id={chartBodyId} className="relative" style={{ display: collapsed ? 'none' : undefined }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: Math.max(180, height - 58), width: '100%' }} autoResize={false}
          onChartReady={(instance) => { instance.group = chartGroup; onChartReady(instance); setRulerChart(instance) }}
          replaceMerge={['series']} lazyUpdate />
        {!collapsed && <ChartRuler chart={rulerChart} series={rulerSeries} resetKey={interval} interval={interval} candleInterval={candleInterval} />}
        {!collapsed && <ChartLevelAlerts chart={rulerChart} metric="oi" series={alertSeries} controls={alertControls} />}
      </div>
    </div>
  )
}

export const MergedOIChart = memo(MergedOIChartComponent)
